// frontend/src/pages/dashboard/BacklogPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { backlogAPI, sprintAPI, projectAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
    Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, useMediaQuery,
    DialogActions, TextField, Chip, IconButton, Tooltip, Grid, Card, useTheme,
    CardContent, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem,
    Skeleton, Fade, alpha
} from '@mui/material';
import {
    Add as AddIcon, DragIndicator as DragIcon,
    MoveToInbox as MoveIcon, Visibility as ViewIcon,
    Close as CloseIcon, Warning as WarningIcon, Dashboard as DashboardIcon, Task as TaskIcon

} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { pollingManager } from '../../hooks/RealtimePoll';

// Modern color palette matching app design
const COLORS = {
    primary: "#4361ee",
    secondary: "#3f37c9",
    success: "#4cc9f0",
    warning: "#f72585",
    info: "#4895ef",
    light: "#f8f9fa",
    dark: "#212529",
    gray: "#6c757d",
    cardBg: "#ffffff",
    surface: "#f0f4f8"
};

const BacklogPage = () => {
    const { projectId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [backlogItems, setBacklogItems] = useState([]);
    const [projectName, setProjectName] = useState('');
    const [sprints, setSprints] = useState([]);
    const [overdueTasks, setOverdueTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [moveDialog, setMoveDialog] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [formData, setFormData] = useState({
        title: '', description: '', priority: 'medium', story_points: '', new_due_date: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [moving, setMoving] = useState(false);
    const [selectedSprintId, setSelectedSprintId] = useState('');
    const [showOverdueOnly, setShowOverdueOnly] = useState(false);
    

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    useEffect(() => {
        return () => {
            pollingManager.stop("backlog");
        };
    }, [projectId]);

    const loadData = async () => {
        // Inside loadData function, add:
        const projectRes = await projectAPI.getById(projectId);
        setProjectName(projectRes.data.name);
        
        try {
            const [backlogRes, sprintsRes, overdueRes] = await Promise.allSettled([
                backlogAPI.getProjectBacklog(projectId),
                sprintAPI.getAll(projectId),
                backlogAPI.getOverdueTasks(projectId)
            ]);

            setBacklogItems(backlogRes.status === 'fulfilled' ? backlogRes.value.data : []);
            setSprints(sprintsRes.status === 'fulfilled' ? sprintsRes.value.data : []);
            setOverdueTasks(overdueRes.status === 'fulfilled' ? (overdueRes.value.data || []) : []);
        } catch (err) {
            toast.error('Failed to load backlog');
            console.error('Backlog load error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await backlogAPI.addToBacklog(projectId, formData);
            toast.success('Task added to backlog');
            setOpenDialog(false);
            setFormData({ title: '', description: '', priority: 'medium', story_points: '', new_due_date: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to add task');
        } finally {
            setSubmitting(false);
        }
    };

    const handleMoveToSprint = async (sprintId) => {
        if (!sprintId) {
            toast.error('Please select a sprint');
            return;
        }

        // ✅ Validate due_date is set
        if (!formData.new_due_date) {
            toast.error('Please set a new due date for the task');
            return;
        }

        setMoving(true);
        try {
            await backlogAPI.moveToSprint(
                projectId,
                selectedTask.id,
                sprintId,
                { due_date: formData.new_due_date }
            );

            toast.success('Task moved to sprint');
            setMoveDialog(false);
            setSelectedSprintId('');
            setFormData(prev => ({ ...prev, new_due_date: '' }));
            loadData();
        } catch (err) {
            console.error('Move to sprint error:', err);
            toast.error(err.response?.data?.error || err.response?.data?.errors?.due_date?.[0] || 'Failed to move task');
        } finally {
            setMoving(false);
        }
    };

    const onDragEnd = async (result) => {
        if (!result.destination) return;

        const reordered = Array.from(backlogItems);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);

        setBacklogItems(reordered);

        try {
            await backlogAPI.reorder(projectId, reordered.map((item, idx) => ({
                id: item.id,
                order: idx
            })));
        } catch (err) {
            toast.error('Failed to reorder');
            loadData();
        }
    };

    // PERMISSION CHECK
    const canView = user?.hasPermission('backlog.view') ||
        user?.hasPermission('sprint.view') ||
        user?.hasPermission('project.view.all');
    const canCreate = user?.hasPermission('backlog.create') ||
        user?.hasPermission('sprint.manage');
    const canManage = user?.hasPermission('sprint.manage') ||
        user?.hasPermission('backlog.move-to-sprint');

    if (!canView) {
        return (
            <Box p={3}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    You do not have permission to view the backlog.
                </Alert>
            </Box>
        );
    }

    // Skeleton Loader Components
    const renderTableSkeletons = () => (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
                <TableHead>
                    <TableRow>
                        {['', 'Task', 'Priority', 'Story Points', 'Assignees', 'Actions'].map((header, i) => (
                            <TableCell key={i} sx={{ fontWeight: 600 }}>
                                <Skeleton variant="text" width={header === 'Task' ? 200 : 80} height={24} />
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5].map((row) => (
                        <TableRow key={row}>
                            <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                            <TableCell>
                                <Skeleton variant="text" width={180} height={20} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width={120} height={14} />
                            </TableCell>
                            <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                            <TableCell><Skeleton variant="text" width={30} height={20} /></TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {[1, 2].map((chip) => (
                                        <Skeleton key={chip} variant="rounded" width={70} height={24} />
                                    ))}
                                </Box>
                            </TableCell>
                            <TableCell align="right">
                                <Skeleton variant="circular" width={32} height={32} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (loading) {
        return (
            <Box sx={{ pl: 3, pt: 8, pr: 3, pb: 6, background: COLORS.surface, minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Box>
                        <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width={150} height={20} />
                    </Box>
                    <Skeleton variant="rounded" width={160} height={48} />
                </Box>
                {renderTableSkeletons()}
            </Box>
        );
    }

    // Combine tasks for display based on filter
    const displayedTasks = showOverdueOnly
        ? overdueTasks.map(t => ({ ...t, is_overdue: true }))
        : backlogItems;

    return (
        <Fade in timeout={400}>
            <Box sx={{ pl: 3, pt: 8, pr: 3, pb: 6, background: COLORS.surface, minHeight: '100vh' }}>

                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                            {projectName || 'Project'} Backlog
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {showOverdueOnly ? overdueTasks.length : backlogItems.length} items
                            {showOverdueOnly && ' (Overdue)'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {/* Overdue Toggle */}
                        {overdueTasks.length > 0 && (
                            <Button
                                variant={showOverdueOnly ? "contained" : "outlined"}
                                size="small"
                                color={showOverdueOnly ? "error" : "primary"}
                                startIcon={<WarningIcon />}
                                onClick={() => setShowOverdueOnly(!showOverdueOnly)}
                                sx={{
                                    textTransform: 'none',                                    
                                    height: 40,
                                    px: 2.5,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    borderColor: showOverdueOnly ? 'error.main' : 'primary.main',
                                    color: showOverdueOnly ? 'white' : 'primary.main',
                                    bgcolor: showOverdueOnly ? 'error.main' : 'transparent',
                                    '&:hover': {
                                    bgcolor: showOverdueOnly ? 'error.dark' : 'primary.lighter',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(67,97,238,0.15)',
                                    },
                                    transition: 'all 0.2s',
                                }}
                            >
                                {showOverdueOnly ? `Overdue (${overdueTasks.length})` :'Show All' }
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<TaskIcon />}
                            onClick={() => navigate(`/tasks?project=${projectId}`)}
                            sx={{
                            height: 40,
                            px: 2.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'primary.lighter',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 8px 25px rgba(67,97,238,0.15)',
                            },
                            textTransform: 'none',
                            transition: 'all 0.2s',
                            }}
                        >
                            Manage Tasks
                        </Button>

                        {canView && (
                            <Button
                                variant="contained"
                                startIcon={<DashboardIcon />}
                                onClick={() => navigate(`/projects/${projectId}/board`)}
                                sx={{
                                    height: 40,
                                    px: 2.5,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                                    },
                                    textTransform: 'none',
                                }}
                            >
                                View Board
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Overdue Tasks Section - Always visible at top when not filtered */}
                {!showOverdueOnly && overdueTasks.length > 0 && (
                    <Paper
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'error.light',
                            bgcolor: alpha('#f72585', 0.03)
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={700} color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningIcon fontSize="small" />
                                Overdue Tasks ({overdueTasks.length})
                            </Typography>
                            <Chip
                                label="Urgent"
                                color="error"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                        </Box>

                        <TableContainer sx={{ maxHeight: 250 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Task</TableCell>
                                        <TableCell>Priority</TableCell>
                                        <TableCell>Due Date</TableCell>
                                        <TableCell>Days Overdue</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {overdueTasks.map(task => (
                                        <TableRow
                                            key={`overdue-${task.id}`}
                                            sx={{
                                                bgcolor: alpha('#f72585', 0.05),
                                                '&:hover': { bgcolor: alpha('#f72585', 0.1) }
                                            }}
                                        >
                                            <TableCell>
                                                <Typography fontWeight={500}>{task.title}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {task.assigned_users?.map(u => u.name).join(', ') || 'Unassigned'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={task.priority}
                                                    color={task.priority === 'urgent' ? 'error' :
                                                        task.priority === 'high' ? 'warning' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography color="error" fontWeight={500}>
                                                    {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={`${task.days_overdue || 0}d`}
                                                    color="error"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {canManage && (
                                                    <Tooltip title="Move to Sprint">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedTask(task);
                                                                setMoveDialog(true);
                                                            }}
                                                            sx={{ color: 'primary.main' }}
                                                        >
                                                            <MoveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Main Backlog Table */}
                {displayedTasks.length === 0 ? (
                    <Alert
                        severity={showOverdueOnly ? "success" : "info"}
                        sx={{ borderRadius: 2 }}
                        icon={showOverdueOnly ? <WarningIcon /> : undefined}
                    >
                        {showOverdueOnly
                            ? '🎉 No overdue tasks - Great job!'
                            : 'No items in backlog.'}
                    </Alert>
                ) : (
                    <Paper
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'error.light',
                            bgcolor: alpha('#dedede', 0.03)
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={700} color="text.primary">
                                Tasks Not in Sprint ({backlogItems.length})
                            </Typography>
                        </Box>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="backlog">
                            {(provided) => (
                                <TableContainer
                                    component={Paper}
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: "grey.50" }}>
                                                <TableCell sx={{ width: 40, fontWeight: 600 }}></TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Story Points</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Assignees</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {displayedTasks.map((item, index) => (
                                                <Draggable
                                                    key={item.id}
                                                    draggableId={String(item.id)}
                                                    index={index}
                                                >
                                                    {(provided) => (
                                                        <TableRow
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            hover
                                                            sx={{
                                                                transition: 'background-color 0.2s',
                                                                '&:hover': { backgroundColor: 'action.hover' },
                                                                bgcolor: item.is_overdue ? alpha('#f72585', 0.03) : 'transparent'
                                                            }}
                                                        >
                                                            <TableCell {...provided.dragHandleProps}>
                                                                <DragIcon fontSize="small" sx={{ cursor: 'grab', color: 'text.secondary' }} />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight={500}>{item.title}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {item.description?.substring(0, 50)}{item.description?.length > 50 ? '...' : ''}
                                                                </Typography>
                                                                {item.is_overdue && item.days_overdue > 0 && (
                                                                    <Typography variant="caption" color="error" fontWeight={600}>
                                                                        ⚠️ {item.days_overdue} days overdue
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={item.priority}
                                                                    size="small"
                                                                    color={item.priority === 'high' || item.priority === 'urgent' ? 'error' : 'default'}
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        textTransform: 'capitalize',
                                                                        bgcolor: item.priority === 'high' || item.priority === 'urgent'
                                                                            ? 'error.lighter' : 'grey.100',
                                                                        color: item.priority === 'high' || item.priority === 'urgent'
                                                                            ? 'error.main' : 'text.primary'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={500}>
                                                                    {item.story_points || 0}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                                    {item.assigned_users?.slice(0, 3).map(u => (
                                                                        <Chip
                                                                            key={u.id}
                                                                            label={u.name?.split(' ')[0]}
                                                                            size="small"
                                                                            sx={{ fontSize: '0.7rem', height: 24 }}
                                                                        />
                                                                    ))}
                                                                    {item.assigned_users?.length > 3 && (
                                                                        <Chip
                                                                            label={`+${item.assigned_users.length - 3}`}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ fontSize: '0.7rem', height: 24 }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="right">
                                                                {canManage && (
                                                                    <Tooltip title="Move to Sprint">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => {
                                                                                setSelectedTask(item);
                                                                                setMoveDialog(true);
                                                                            }}
                                                                            sx={{
                                                                                bgcolor: 'primary.lighter',
                                                                                color: 'primary.main',
                                                                                '&:hover': {
                                                                                    bgcolor: 'primary.light',
                                                                                    transform: 'translateY(-1px)'
                                                                                },
                                                                                transition: 'all 0.2s'
                                                                            }}
                                                                        >
                                                                            <MoveIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Droppable>
                        </DragDropContext>
                    </Paper>
                )}

                {/* Add to Backlog Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }
                    }}
                >
                    <DialogTitle sx={{
                        pb: 1, fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', gap: 1
                    }}>
                        <AddIcon sx={{ color: COLORS.primary }} />
                        Add to Backlog
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <TextField
                            label="Task Title *"
                            fullWidth
                            margin="normal"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="What needs to be done?"
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            margin="normal"
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Add more details about this task..."
                            InputProps={{ sx: { borderRadius: 2 } }}
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                label="Priority"
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="low">🟢 Low</MenuItem>
                                <MenuItem value="medium">🟡 Medium</MenuItem>
                                <MenuItem value="high">🟠 High</MenuItem>
                                <MenuItem value="urgent">🔴 Urgent</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Story Points"
                            type="number"
                            fullWidth
                            margin="normal"
                            value={formData.story_points}
                            onChange={(e) => setFormData({ ...formData, story_points: e.target.value })}
                            placeholder="Estimate effort (1-13)"
                            InputProps={{ sx: { borderRadius: 2 }, inputProps: { min: 0, max: 13, step: 1 } }}
                        />
                    </DialogContent>
                    <DialogActions sx={{
                        p: 2.5, borderTop: '1px solid', borderColor: 'divider',
                        bgcolor: 'grey.50', borderRadius: '0 0 16px 16px'
                    }}>
                        <Button
                            onClick={() => setOpenDialog(false)}
                            color="inherit"
                            variant="outlined"
                            disabled={submitting}
                            sx={{
                                px: 3, py: 1.2, borderRadius: 3, fontWeight: 600, textTransform: 'none',
                                border: '2px solid', borderColor: 'grey.300', color: 'grey.700',
                                '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.100', transform: 'translateY(-1px)' },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || !formData.title?.trim()}
                            sx={{
                                px: 3, py: 1.2, borderRadius: 3, fontWeight: 700, textTransform: 'none',
                                background: 'linear-gradient(135deg, #4361ee 0%, #3f37c9 100%)',
                                boxShadow: '0 4px 15px rgba(67,97,238,0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #3f37c9 0%, #4361ee 100%)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {submitting ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Adding...</> : 'Add to Backlog'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Move to Sprint Dialog */}
                <Dialog
                    open={moveDialog}
                    onClose={() => setMoveDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }
                    }}
                >
                    <DialogTitle sx={{
                        pb: 1, fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider',
                        display: 'flex', alignItems: 'center', gap: 1
                    }}>
                        <MoveIcon sx={{ color: COLORS.info }} />
                        Move to Sprint
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Select sprint to move "<strong>{selectedTask?.title}</strong>" to:
                        </Typography>
                        <FormControl fullWidth>
                            <InputLabel>Sprint</InputLabel>
                            <Select
                                value={selectedSprintId}
                                onChange={(e) => setSelectedSprintId(e.target.value)}
                                label="Sprint"
                                sx={{ borderRadius: 2 }}
                                disabled={moving}
                            >
                                {sprints.filter(s => s.status === 'planning' || s.status === 'active').map(sprint => (
                                    <MenuItem key={sprint.id} value={sprint.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={sprint.status}
                                                size="small"
                                                color={sprint.status === 'active' ? 'success' : 'default'}
                                                sx={{ fontSize: '0.65rem', height: 20 }}
                                            />
                                            {sprint.name}
                                        </Box>
                                    </MenuItem>
                                ))}
                                {sprints.filter(s => s.status === 'planning' || s.status === 'active').length === 0 && (
                                    <MenuItem disabled>No active sprints available</MenuItem>
                                )}
                            </Select>

                            <TextField
                                label="New Due Date *"
                                type="datetime-local"
                                value={formData.new_due_date}
                                onChange={(e) => setFormData({ ...formData, new_due_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                required
                                error={!formData.new_due_date && moveDialog}
                                helperText={!formData.new_due_date && moveDialog ? "Due date is required" : ""}
                                sx={{ mt: 2 }}
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{
                        p: 2.5, borderTop: '1px solid', borderColor: 'divider',
                        bgcolor: 'grey.50', borderRadius: '0 0 16px 16px'
                    }}>
                        <Button
                            onClick={() => setMoveDialog(false)}
                            color="inherit"
                            variant="outlined"
                            disabled={moving}
                            sx={{
                                px: 3, py: 1.2, borderRadius: 3, fontWeight: 600, textTransform: 'none',
                                border: '2px solid', borderColor: 'grey.300', color: 'grey.700',
                                '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.100', transform: 'translateY(-1px)' },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => selectedSprintId && handleMoveToSprint(selectedSprintId)}
                            disabled={moving || !selectedSprintId}
                            sx={{
                                px: 3, py: 1.2, borderRadius: 3, fontWeight: 700, textTransform: 'none',
                                background: 'linear-gradient(135deg, #4895ef 0%, #3f37c9 100%)',
                                boxShadow: '0 4px 15px rgba(72,149,239,0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #3f37c9 0%, #4895ef 100%)',
                                    transform: 'translateY(-2px)',
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {moving ? <><CircularProgress size={18} sx={{ mr: 1 }} /> Moving...</> : 'Move Task'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Fade>
    );
};

export default BacklogPage;